import { normalizeText, cleanName, qualityOf } from './utils.js';

/* Calidad adaptable sin que el proveedor la ofrezca: la mayoría emite cada canal en una sola calidad y en su lugar da
   varios canales (La 1 SD / HD / FHD). Aquí se agrupan esas «variantes» del mismo canal, se estima qué caudal necesita
   cada calidad y se decide con cuál abrir según la conexión medida y cuándo bajar por cortes repetidos. Lógica pura,
   sin DOM, para poder probarla. */

const QUAL_RE = /\b(uhd|4k|2160p?|fhd|full\s*hd|1080[pi]?|hd|720p?|sd|480p?|576[pi]?)\b/g;
const RANK = { UHD: 4, FHD: 3, HD: 2, SD: 1 };
// Caudal orientativo (kb/s) para ver cada calidad sin cortes, con margen. Los canales sin etiqueta se tratan como HD.
const NEED = { UHD: 20000, FHD: 7000, HD: 4000, SD: 1800 };

/** Nombre del canal sin país ni calidad: dos canales con la misma clave son variantes del mismo canal. */
export function variantKey(name) {
    return normalizeText(cleanName(name || '')).replace(QUAL_RE, ' ').replace(/[()[\]|_:/\\-]+/g, ' ').replace(/\s+/g, ' ').trim();
}

export function qualityRank(name) {
    return RANK[qualityOf(name)] || 0;
}

/** kb/s que hace falta para esa calidad. */
export function needKbps(name) {
    return NEED[qualityOf(name)] || NEED.HD;
}

/** Variantes ordenadas de mejor a peor calidad (las sin etiqueta, al final). */
export function sortVariants(list) {
    return list.slice().sort((a, b) => qualityRank(b.name) - qualityRank(a.name) || String(a.name).localeCompare(String(b.name)));
}

/** Etiqueta corta de una variante para el menú de calidad. */
export function variantLabel(ch) {
    return qualityOf(ch.name) || cleanName(ch.name);
}

/**
 * Con qué variante abrir. `mode`: auto (según la conexión; nunca sube por su cuenta, solo baja si no da) | best (la mejor
 * siempre) | saver (la más ligera). `estimateKbps` 0 = desconocido (en auto se respeta lo elegido).
 * → la variante a usar (puede ser `current`).
 */
export function pickVariant({ variants, current, estimateKbps = 0, mode = 'auto' }) {
    const list = sortVariants(variants || []);
    if (list.length < 2 || !current) return current;
    if (mode === 'best') return list[0];
    if (mode === 'saver') return list[list.length - 1];
    if (!estimateKbps) return current;
    const fits = (ch) => needKbps(ch.name) <= estimateKbps * 0.9;
    if (fits(current)) return current;
    // De las peores que la actual, la mejor que quepa; si ninguna cabe, la más ligera.
    const lower = list.filter((ch) => qualityRank(ch.name) < qualityRank(current.name));
    return lower.find(fits) || lower[lower.length - 1] || current;
}

/** La variante inmediatamente peor que `current` (o null). */
export function lowerVariant(variants, current) {
    const list = sortVariants(variants || []);
    const i = list.findIndex((ch) => ch.id === current.id);
    return i >= 0 && i + 1 < list.length ? list[i + 1] : null;
}

/**
 * Cortes recientes: ¿toca bajar de calidad? Tres paradas a cargar en dos minutos, o una muy larga, con un respiro de
 * 60 s tras el último cambio para no bajar en cadena.
 * `stalls`: [{ at, long }] ; → true si hay que bajar.
 */
export function shouldDowngrade(stalls, { now = Date.now(), lastSwitchAt = 0, windowMs = 120000, cooldownMs = 60000 } = {}) {
    if (now - lastSwitchAt < cooldownMs) return false;
    const recent = (stalls || []).filter((s) => now - s.at <= windowMs);
    const weight = recent.reduce((n, s) => n + (s.long ? 2 : 1), 0);
    return weight >= 3;
}
